import { create } from 'zustand';

import * as datadogRum from 'lib/telemetry/datadogRum';
import type { ServerError } from 'models';
import type { ReferralOfferResult } from 'models/reqres';
import type { ClaimRevealResult } from 'queries/referralsMutations';
import { getReferralOffer } from 'transport/domains/referrals';
import { noop } from 'utils/noop';
import {
    clearReferral as clearReferralCarriers,
    normalizeReferralCode,
    persistCapturedReferral,
    persistValidatedReferral,
    readDurableReferral,
} from 'utils/referralAttribution';

const ERROR_KINDS = [
    'cancelled',
    'timeout',
    'notFound',
    'internal',
    'unavailable',
    'maintenance',
    'tooManyRequests',
    'unauthorized',
    'forbidden',
    'invalid',
    'conflict',
] as const satisfies readonly (keyof ServerError)[];

type ValidationSource = 'url' | 'cookie' | 'signup_page' | 'manual_entry';

type Verdict = 'valid' | 'invalid' | 'unverified';

interface OfferOutcome {
    result?: ReferralOfferResult;
    err?: ServerError;
}

function classifyOutcome({ result, err }: OfferOutcome): Verdict {
    if (err) {
        return err.notFound ? 'invalid' : 'unverified';
    }
    if (result?.valid === true) return 'valid';
    return result?.valid === false ? 'invalid' : 'unverified';
}

// svc-api rejects codes over 50 runes, so this only clips pasted blobs.
const MAX_REPORTED_CODE_LENGTH = 64;

function reportReferralValidation(
    code: string,
    outcome: Verdict,
    source: ValidationSource,
    extra: { err?: ServerError; deduped?: boolean; suppressed?: boolean } = {},
): void {
    const { err, deduped = false, suppressed = false } = extra;
    datadogRum.addAction('referral_code_validation', {
        referral_code: code.slice(0, MAX_REPORTED_CODE_LENGTH),
        outcome,
        source,
        error_kind: err ? (ERROR_KINDS.find((kind) => err[kind]) ?? 'unknown') : undefined,
        deduped,
        suppressed,
    });
}

/** Reports a validation run outside this store, for surfaces that own their own lookup. */
export function reportReferralOfferOutcome(code: string, source: ValidationSource, outcome: OfferOutcome): void {
    reportReferralValidation(code, classifyOutcome(outcome), source, {
        err: outcome.err,
        suppressed: !!outcome.err?.cancelled,
    });
}

// transport/core.ts cancels a repeat of an identical request while the tab is hidden.
const inFlightOffers = new Map<string, Promise<OfferOutcome>>();

function fetchOfferOnce(code: string): { pending: Promise<OfferOutcome>; deduped: boolean } {
    const existing = inFlightOffers.get(code);
    if (existing) {
        return { pending: existing, deduped: true };
    }

    const pending = new Promise<OfferOutcome>((resolve) => {
        getReferralOffer(
            code,
            (result) => resolve({ result }),
            (err) => resolve({ err }),
        );
    });
    inFlightOffers.set(code, pending);
    void pending.finally(() => {
        if (inFlightOffers.get(code) === pending) {
            inFlightOffers.delete(code);
        }
    });

    return { pending, deduped: false };
}

// Variable offers carry no flat referralAmountCents, so maxReferralRewardCents also counts.
function isOfferResolved(state: ReferralState, code: string): boolean {
    return state.referral === code && (!!state.referralAmountCents || !!state.maxReferralRewardCents);
}

interface ReferralInfo {
    referral: string | undefined;
    referralAmountCents: number | undefined;
    referrerFirstName?: string;
    wasReferredUnderVariableRewards?: boolean;
    maxReferralRewardCents?: number;
    /** Trade volume (cents) the referee must reach to unlock their reward. */
    tradeThresholdCents?: number;
}

interface ReferralActions {
    referralBonusSheetOpened: () => void;
    referralBonusSheetClosed: () => void;
    vaultOpened: () => void;
    vaultClosed: () => void;
    /** Sets referral info directly — used once the caller already has a validated result. Persists the referral via `persistValidatedReferral`. */
    setReferralInfo: (info: ReferralInfo) => void;
    /** Clears referral state. With `code`, only removes the durable carrier if it holds that code; omit to clear unconditionally. */
    clearReferral: (code?: string) => void;
    /**
     * Sets a referral code, validating it against the API. Persists the referral via
     * `persistValidatedReferral` and pushes to `dataLayer` once validated; clears everything on
     * an invalid code. An empty input clears; an oversized input is ignored, not cleared.
     */
    setReferral: (referral: string | undefined) => void;
    /** Reads the durable referral (localStorage, falling back to the cookie) and validates it. Client-only, call once at app boot. */
    initFromCookie: () => void;
    /**
     * Validates a referral code against the API and stores the result on success. Invalid or
     * not-found codes clear the referral and flag `referralCodeInvalid`; an inconclusive
     * response flags `referralCodeUnverified` unless the code is already resolved.
     */
    loadIsValidReferralCode: (referralCode: string, onSuc?: (result: ReferralOfferResult) => void) => void;
    /** Records that the invalid/unverified notice has been surfaced for `code`. */
    referralNoticeShown: (code: string) => void;
    /** Publishes a claim's revealed reward for the vault sheet to display. */
    claimResultReceived: (claimResult: ClaimRevealResult) => void;
    claimResultCleared: () => void;
}

interface ReferralState {
    referral: string | undefined;
    referralAmountCents: number | undefined;
    referrerFirstName: string | undefined;
    wasReferredUnderVariableRewards: boolean;
    maxReferralRewardCents: number | undefined;
    tradeThresholdCents: number | undefined;
    referralCodeInvalid: boolean;
    /** Code `referralCodeInvalid` refers to — lets `resolveReferralForSubmission` skip it if it's still sitting in the URL/durable storage. */
    invalidatedReferralCode: string | undefined;
    referralCodeUnverified: boolean;
    /** Number of validations awaiting a response; the UI must not judge a code until this is 0. */
    validationsInFlight: number;
    /** Code the invalid/unverified notice was last shown for, so remounts don't re-toast. */
    referralNoticeShownForCode: string | undefined;
    referralBonusProgressSheetOpen: boolean;
    vaultModalOpen: boolean;
    /** Result of the most recent referral claim — drives the vault sheet's reveal. */
    claimResult: ClaimRevealResult | null;
    actions: ReferralActions;
}

export const useReferralStore = create<ReferralState>()((set, get) => {
    const inconclusive: ReferralOfferResult = { valid: undefined, isVariable: false };

    const applyOutcome = (
        code: string,
        source: ValidationSource,
        { result, err }: OfferOutcome,
        deduped: boolean,
    ): ReferralOfferResult => {
        const { actions, referral: current } = get();
        // Apply only while this lookup is still the active referral. Every dispatcher sets
        // `referral` to the code before awaiting, so `current !== code` — including a cleared
        // undefined — means a newer set or a clear superseded this response; drop it rather
        // than let a stale valid re-add a removed code or a stale failure flag it.
        if (current !== code) {
            return inconclusive;
        }

        const verdict = classifyOutcome({ result, err });

        if (verdict === 'invalid') {
            reportReferralValidation(code, 'invalid', source, { err, deduped });
            actions.clearReferral(code);
            set({ referralCodeInvalid: true, referralCodeUnverified: false, invalidatedReferralCode: code });
            return { valid: false, isVariable: false };
        }

        if (verdict === 'unverified') {
            // A cancellation carries no verdict, and a resolved offer is authoritative.
            const suppressed = err?.cancelled || isOfferResolved(get(), code);
            reportReferralValidation(code, 'unverified', source, { err, deduped, suppressed });
            if (!suppressed) {
                set({ referralCodeUnverified: true });
            }
            return inconclusive;
        }

        const offer = result ?? inconclusive;
        reportReferralValidation(code, 'valid', source, { deduped });
        actions.setReferralInfo({
            referral: code,
            referralAmountCents: offer.refereeCreditCents,
            referrerFirstName: offer.referrerFirstName,
            wasReferredUnderVariableRewards: offer.isVariable,
            maxReferralRewardCents: offer.maxReferralRewardCents,
            tradeThresholdCents: offer.tradeThresholdCents,
        });
        return offer;
    };

    const validateReferralCode = async (code: string, source: ValidationSource): Promise<ReferralOfferResult> => {
        const { pending, deduped } = fetchOfferOnce(code);
        set((state) => ({ validationsInFlight: state.validationsInFlight + 1 }));
        try {
            return applyOutcome(code, source, await pending, deduped);
        } finally {
            set((state) => ({ validationsInFlight: state.validationsInFlight - 1 }));
        }
    };

    return {
        referral: undefined,
        referralAmountCents: undefined,
        referrerFirstName: undefined,
        wasReferredUnderVariableRewards: false,
        maxReferralRewardCents: undefined,
        tradeThresholdCents: undefined,
        referralCodeInvalid: false,
        invalidatedReferralCode: undefined,
        referralCodeUnverified: false,
        validationsInFlight: 0,
        referralNoticeShownForCode: undefined,
        referralBonusProgressSheetOpen: false,
        vaultModalOpen: false,
        claimResult: null,

        actions: {
            referralBonusSheetOpened: () => set({ referralBonusProgressSheetOpen: true }),
            referralBonusSheetClosed: () => set({ referralBonusProgressSheetOpen: false }),
            vaultOpened: () => set({ vaultModalOpen: true }),
            vaultClosed: () => set({ vaultModalOpen: false }),

            setReferralInfo: (info) => {
                // Only called with an already-validated result, so the code is valid — clear any
                // lingering invalid/unverified flags a prior attempt may have set.
                set({
                    referral: info.referral,
                    referralAmountCents: info.referralAmountCents,
                    referrerFirstName: info.referrerFirstName,
                    wasReferredUnderVariableRewards: info.wasReferredUnderVariableRewards ?? false,
                    maxReferralRewardCents: info.maxReferralRewardCents,
                    tradeThresholdCents: info.tradeThresholdCents,
                    referralCodeInvalid: false,
                    invalidatedReferralCode: undefined,
                    referralCodeUnverified: false,
                });
                // registrationStore reads the store first, then falls back to this cookie.
                if (info.referral) {
                    persistValidatedReferral(info.referral);
                }
            },

            clearReferral: (code) => {
                set({
                    referral: undefined,
                    referralAmountCents: undefined,
                    referrerFirstName: undefined,
                    wasReferredUnderVariableRewards: false,
                    maxReferralRewardCents: undefined,
                    tradeThresholdCents: undefined,
                });
                clearReferralCarriers(code);
            },

            setReferral: (referral) => {
                const { actions } = get();

                if (!referral?.trim()) {
                    actions.clearReferral();
                    return;
                }

                // Normalize-rejected (oversized) input: ignore it, never clear an existing referral.
                const normalizedReferral = normalizeReferralCode(referral);
                if (!normalizedReferral) {
                    return;
                }

                if (isOfferResolved(get(), normalizedReferral)) {
                    return;
                }

                // Clear the resolved fields too, so isOfferResolved can't read a stale offer.
                set({
                    referral: normalizedReferral,
                    referralAmountCents: undefined,
                    referrerFirstName: undefined,
                    wasReferredUnderVariableRewards: false,
                    maxReferralRewardCents: undefined,
                    tradeThresholdCents: undefined,
                    referralCodeInvalid: false,
                    invalidatedReferralCode: undefined,
                    referralCodeUnverified: false,
                });
                persistCapturedReferral(normalizedReferral);

                void validateReferralCode(normalizedReferral, 'url').then((offer) => {
                    if (offer.valid !== true || typeof window === 'undefined') return;
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ referral: normalizedReferral });
                });
            },

            initFromCookie: () => {
                if (typeof document === 'undefined') return;

                const referralCode = readDurableReferral();
                if (!referralCode) return;

                const state = get();
                // A code already in the store came from the URL — fresher intent than the cookie.
                if (
                    (state.referral !== undefined && state.referral !== referralCode) ||
                    isOfferResolved(state, referralCode)
                ) {
                    return;
                }
                set({ referral: referralCode });

                setTimeout(() => {
                    void validateReferralCode(referralCode, 'cookie');
                }, 0);
            },

            loadIsValidReferralCode: (referralCode, onSuc = noop) => {
                void validateReferralCode(referralCode, 'signup_page').then(onSuc);
            },

            referralNoticeShown: (code) => set({ referralNoticeShownForCode: code }),

            claimResultReceived: (claimResult) => set({ claimResult }),
            claimResultCleared: () => set({ claimResult: null }),
        },
    };
});
